/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-param-reassign */
import { datadogRum } from '@datadog/browser-rum'
import {
  createAsyncThunk,
  createSlice,
  Draft,
  PayloadAction,
} from '@reduxjs/toolkit'
import {
  SessionDataArgs,
  ICheckoutRules,
  SessionCheckoutRuleValue,
} from '../../../graphQL/queries/getSession/types'
import {
  DefaultCheckoutRulesNames,
  PREFILL_CART_MAX_LIMIT,
} from '../../../consts/defaultConfigValues/defaultConstants'
import {
  resolveHidePrices,
  isHidePricesMisconfigured,
} from '../../../utils/hidePrices'
import { isPackageSortMisconfigured } from '../../../utils/packageSort'
import { isRecommendationLimitsMisconfigured } from '../../../utils/recommendationLimits'
import {
  getPrefillCartLimitIssue,
  PrefillCartLimitIssue,
} from '../../../utils/prefillCartLimit'
import { findMisplacedCartCheckoutSummaries } from '../../../utils/cartCheckoutSummaryPlacement'
import {
  IConfig,
  IModals,
  ISliceDefault,
  AlertConstrains,
  LoaderConfig,
  LoadersType,
  ErrorMessageTranslation,
  IAlert,
  IGetConfigArgs,
  IApplyConfigForSessionArgs,
  IRefetchConfigArgs,
  CheckoutRuleDocumentType,
} from '../../../interfaces'
import {
  IModalActionsProps,
  IDialogActionsProps,
} from '../../../interfaces/actions'
import getConfig from '../../../services/getConfig'
import processConfig from '../../../utils/processConfig'
import { generateDataDogContext } from '../../../utils/dataDog'
import castSessionNotifications from '../../../utils/castNotifications'
import getUrlParams from '../../../utils/getUrlParams'
import flatTexts from '../../../utils/flatTexts'
import { writeCachedTheme } from '../../../utils/bootTheme'
import customerCodeForConfig from '../../../utils/configCustomerCode'

export const initialState: ISliceDefault = {
  loaders: [],
  loading: true,
  config: undefined,
  loadingDelay: 1000,
  sessionId: undefined,
  dialogs: undefined,
  storeName: undefined,
  configRequestId: undefined,
  configSlug: undefined,
  validators: [],
  currentModalItem: '',
  errorMessage: null,
  globalError: false,
  showErrorTitle: false,
  onErrorCallback: undefined,
  bootErrorRequestId: undefined,
  excludedUiComponents: [],
  loyaltyPointsToEarn: null,
  cartAlerts: {
    [AlertConstrains.cartConditionPendingMinAmount]: {
      isActive: false,
    },
    [AlertConstrains.cartConditionPendingMinQty]: {
      isActive: false,
    },
    [AlertConstrains.cartConditionPendingMaxAmount]: {
      isActive: false,
    },
    [AlertConstrains.cartConditionPendingMaxQty]: {
      isActive: false,
    },
    [AlertConstrains.creditLimitMaxAmount]: {
      isActive: false,
    },
  },
  params: {},
  showRecommendations: false,
  showSuggestedItems: false,
  alerts: [],
  sessionNotifications: {
    STOCK_AVAILABLE: false,
    AUTHENTICATION_NEEDED: false,
    SESSION_CREATED: false,
    ORDER_REMINDER: false,
    ABANDONED_CART: false,
  },
  loginStatus: 'idle',
  loginError: undefined,
  loginRateLimitRetryAfterSeconds: undefined,
}

/**
 * Whether `requestId` belongs to a `fetchConfig` that a later one has
 * replaced. Unset means no load has gone through `pending` yet, so a
 * hand-dispatched `fetchConfig.fulfilled` (as some tests build) lands. Once a
 * real load has run `pending`, a hand-built `fulfilled` whose id does not
 * match, or that carries no `meta`, is dropped like any superseded load.
 */
const isSupersededConfigLoad = (
  state: Pick<ISliceDefault, 'configRequestId'>,
  requestId: string | undefined,
) => state.configRequestId !== undefined && state.configRequestId !== requestId

/**
 * Whether a `refetchConfigForCustomer` started under `base` no longer describes
 * the store: a `fetchConfig` has started since (slug change), or the session
 * has moved under the same slug.
 */
const isStaleCustomerConfigLoad = (
  state: Pick<ISliceDefault, 'configRequestId' | 'sessionId'>,
  base: { baseRequestId?: string; baseSessionId?: string },
) =>
  isSupersededConfigLoad(state, base.baseRequestId) ||
  state.sessionId !== base.baseSessionId

/** What `setSessionRulePick` carries: one rule type and the value written. */
export interface SessionRulePickPayload {
  ruleType: CheckoutRuleDocumentType
  value: SessionCheckoutRuleValue
}

/**
 * Writes a loaded config and recomputes `hidePricesActive` against the session
 * already in the store. Shared by both config loads so neither can skip the
 * recompute.
 */
const applyConfig = (state: Draft<ISliceDefault>, config: IConfig) => {
  state.config = config as Draft<IConfig>

  if (state.config?.options) {
    state.config.options.hidePricesActive = resolveHidePrices(
      state.config.options.hidePrices,
      state.sessionData?.customer?.customFields,
    )
  }
}

export const defaultSlice = createSlice({
  name: 'defaultSlice',
  initialState,
  reducers: {
    manageErrors: (
      state,
      {
        payload: {
          errorMessageTranslation,
          globalError,
          showErrorTitle,
          onErrorCallback,
          requestId,
        },
      }: PayloadAction<{
        errorMessageTranslation: ErrorMessageTranslation
        globalError?: boolean
        showErrorTitle?: boolean
        onErrorCallback?: () => void
        requestId?: string
      }>,
    ) => {
      let errorMessage: string = 'An error has occurred'
      const errorsTexts = flatTexts(state.config?.texts?.errors)
      let callback
      if (errorMessageTranslation) {
        switch (errorMessageTranslation) {
          case ErrorMessageTranslation.CatalogueEmptyData:
            errorMessage =
              (errorsTexts?.catalogueEmptyData as string) || 'Catalogue empty'
            break
          case ErrorMessageTranslation.InitialData:
            errorMessage =
              (errorsTexts?.initialData as string) || 'Session expired'
            break
          case ErrorMessageTranslation.GetCart:
            errorMessage =
              (errorsTexts?.getCart as string) || 'Error on get cart'
            break
          case ErrorMessageTranslation.NonProvidedParams:
            errorMessage =
              (errorsTexts?.nonProvidedParams as string) || 'No params provided'
            break
          case ErrorMessageTranslation.ErrorOnInitialLoad:
            errorMessage =
              errorsTexts?.tryAgainIntruction || 'An error has ocurred'
            callback = onErrorCallback
            break
          default:
            break
        }
      }
      state.onErrorCallback = callback
      state.errorMessage = errorMessage
      state.globalError = !!globalError
      state.showErrorTitle = !!showErrorTitle
      // Tag the boot error with the `fetchConfig` that raised it, so a later
      // successful load can tell "its own" boot error apart from an unrelated
      // one. The tag must describe the error currently on screen, so any
      // other `manageErrors` call — a different translation (even with
      // `globalError: true`), or the `useGetCart` / `useGetSummary` reset
      // path dropping the flag — clears the slot. Otherwise a stale tag
      // would survive the replacement and the next successful config load
      // would wipe an unrelated error it had no business clearing.
      if (
        errorMessageTranslation ===
          ErrorMessageTranslation.ErrorOnInitialLoad &&
        globalError &&
        requestId
      ) {
        state.bootErrorRequestId = requestId
      } else {
        state.bootErrorRequestId = undefined
      }
    },
    addLoader: (state, action: PayloadAction<LoaderConfig>) => {
      const { payload } = action
      if (typeof payload === 'string') {
        state.loaders = [...state.loaders, payload]
      } else {
        state.loaders = [...state.loaders, payload.name]
        state.loadingDelay = payload.delay
        state.loadingDelayOwner = payload.name
      }
      state.loading = true
    },
    /**
     * Puts the splash debounce back to a value read earlier. For a loader
     * raised with the object form, which writes `loadingDelay`, on a path
     * where the page stays alive afterwards (`reloadPage`, COM-1521). It
     * clears `loadingDelayOwner`: the value put back belongs to no loader.
     * The caller checks the owner first, so it puts back only a debounce
     * no other loader has written since (COM-1581).
     */
    setLoadingDelay: (state, action: PayloadAction<number>) => {
      state.loadingDelay = action.payload
      state.loadingDelayOwner = undefined
    },
    removeLoader: (state, action: PayloadAction<string>) => {
      const index = state.loaders.indexOf(action.payload)
      const newLoaders = [...state.loaders]
      newLoaders.splice(index, 1)
      state.loaders = newLoaders
      state.loading = newLoaders.length > 0
    },
    setSessionData: (state, action: PayloadAction<SessionDataArgs>) => {
      const { payload } = action
      if (
        !payload.configuration?.checkoutRules?.cartConditionPendingMinAmount
          .isActive
      ) {
        state.excludedUiComponents = [
          DefaultCheckoutRulesNames.MIN_AMOUNT_CART_CHECKOUT_RULE,
        ]
      }
      if (payload?.configuration?.notifications) {
        state.sessionNotifications = castSessionNotifications(
          payload?.configuration?.notifications,
        )
      }
      state.sessionData = payload
      if (state.config?.options) {
        state.config.options.hidePricesActive = resolveHidePrices(
          state.config.options.hidePrices,
          payload.customer?.customFields,
        )
      }
      const { frequentlyBoughtTogether, suggestedItems } =
        payload.configuration?.recommendedProducts || {}
      const showRecommendations = !!payload.configuration?.showRecommendations
      const showFrequentlyBoughtTogetherItems =
        frequentlyBoughtTogether === 'ML' || showRecommendations
      const showSuggestedItems = suggestedItems === 'ML' || showRecommendations
      state.showRecommendations = showFrequentlyBoughtTogetherItems
      state.showSuggestedItems = showSuggestedItems
    },
    setCheckoutRules: (state, { payload }: PayloadAction<ICheckoutRules>) => {
      if (state?.sessionData?.configuration?.checkoutRules && payload) {
        state.sessionData.configuration.checkoutRules = {
          ...state.sessionData.configuration.checkoutRules,
          ...payload,
        }
      }
    },
    /**
     * Records a confirmed checkout-rule pick on the session snapshot (COM-1765).
     *
     * `sessionData.checkoutRules` is otherwise written only by `setSessionData`
     * (boot and catalog refresh), so without this it keeps the page-load pick
     * after a write has replaced it on the server, and every reader that falls
     * back to it (`selectRulePick`, `selectRulePickId`, the calendar's
     * `toSelectedDate`) resurrects that pick once `checkoutSlice.selections` is
     * cleared. Dispatched by `useCheckoutRuleSelection.select`, only after
     * `setCheckoutRuleSession` resolved.
     *
     * **Mirrors the server's `setCheckoutRuleById`: filter out the type, then
     * append**, so the array keeps at most one entry per `ruleType`, which
     * `readSessionRulePick` relies on.
     *
     * **Narrow on purpose.** It touches `sessionData.checkoutRules` and nothing
     * else. `setSessionData` would also recompute `hidePricesActive` (moving
     * `config`'s identity and re-running `Config`'s effects),
     * `excludedUiComponents`, the notifications and the recommendation flags.
     *
     * A no-op without `sessionData`: a session-less route, or a boot that has
     * not answered yet. Creating a partial object there would read as a booted
     * session to every consumer of the field, and the boot read that follows
     * carries the server's copy anyway.
     */
    setSessionRulePick: (
      state,
      { payload }: PayloadAction<SessionRulePickPayload>,
    ) => {
      if (!state.sessionData) {
        return
      }
      const others = (state.sessionData.checkoutRules ?? []).filter(
        (rule) => rule?.ruleType !== payload.ruleType,
      )
      state.sessionData.checkoutRules = [
        ...others,
        { ruleType: payload.ruleType, value: payload.value },
      ]
    },
    /**
     * Forgets one rule type's pick on the session snapshot (COM-1765).
     *
     * For `selection_not_offered` recovery (`useCheckoutRuleRedirect`): the
     * server named the pick it holds as one it no longer offers, so neither
     * that pick nor an older page-load one is worth showing. With the entry
     * gone the pickers mark nothing, the cart rows and the confirm dialog
     * show no pick, the rule reads unsettled, and auto-settle takes its write
     * path rather than settling on a value nobody re-confirmed.
     *
     * The server may still hold the rejected value until the shopper picks
     * again; "nothing" is the honest client-side reading of a pick the
     * storefront just refused.
     */
    dropSessionRulePick: (
      state,
      { payload }: PayloadAction<{ ruleType: CheckoutRuleDocumentType }>,
    ) => {
      if (!state.sessionData?.checkoutRules) {
        return
      }
      state.sessionData.checkoutRules = state.sessionData.checkoutRules.filter(
        (rule) => rule?.ruleType !== payload.ruleType,
      )
    },
    /**
     * Writes the points-to-earn number for the current cart (COM-1397).
     *
     * `null` collapses three cases the widget treats the same way: the
     * backend returned `null`, it returned `0`, or the query errored (an
     * unopted-in storefront answers "must be setup as an add-on"). The
     * caller — `useLoyaltyPointsPreflight` — maps all three to `null`
     * before dispatching, so this reducer stays naive.
     */
    setLoyaltyPointsToEarn: (
      state,
      { payload }: PayloadAction<number | null>,
    ) => {
      state.loyaltyPointsToEarn = payload
    },
    setSessionId: (state, { payload }) => {
      state.sessionId = payload
    },
    updateParams: (state, { payload }) => {
      state.params = payload
    },
    openDialog: (state, action: PayloadAction<IDialogActionsProps>) => {
      const {
        icon,
        title,
        message,
        buttons,
        id,
        details,
        hidePrices,
        currency,
        currencyCode,
      } = action.payload
      /*
       * Assigned whole rather than merged, so the four money-and-rows props a
       * previous dialog set cannot survive into the next one. `closeDialog`
       * rebuilds this object the same way and therefore clears them too.
       */
      state.dialogs = {
        show: true,
        icon: icon || { name: '' },
        title: title || { value: '' },
        message: message || { value: '' },
        buttons,
        id,
        details,
        hidePrices,
        currency,
        currencyCode,
      }
    },
    closeDialog: (state, action: PayloadAction<IDialogActionsProps>) => {
      state.dialogs = {
        show: false,
        icon: { name: '' },
        title: { value: '' },
        message: { value: '' },
        buttons: [],
      }
    },
    openModal: (state, action: PayloadAction<IModalActionsProps>) => {
      if (state.modals) {
        const { container, state: modalState = {}, item = '' } = action.payload
        state.currentModalItem = item
        state.modals[container] = {
          show: true,
          state: modalState,
        }
      }
    },
    closeModal: (state, action: PayloadAction<IModalActionsProps>) => {
      if (state.modals) {
        const { container, state: modalState } = action.payload
        if (container === 'all') {
          Object.keys(state.modals).forEach((modalKey) => {
            if (state.modals) {
              state.modals[modalKey] = {
                ...state.modals[modalKey],
                show: false,
              }
            }
          })
        } else {
          state.modals[container] = {
            show: false,
            state: modalState || state.modals[container]?.state,
          }
        }
      }
    },
    setModals: (state, { payload }) => {
      state.modals = payload
    },
    /**
     * Puts the boot config and its modals back when `sessionId` is still the
     * store's session and a customer config is applied (COM-1709), so a later
     * session never bakes under an earlier customer's config.
     */
    restoreBootConfig: (
      state,
      { payload }: PayloadAction<{ sessionId: string }>,
    ) => {
      const { bootConfig } = state

      if (
        !state.customerConfigSessionId ||
        !bootConfig ||
        state.sessionId !== payload.sessionId
      ) {
        return
      }

      // Copies, so recomputing `hidePricesActive` does not write into `bootConfig`.
      applyConfig(state, {
        ...bootConfig,
        options: { ...bootConfig.options },
      } as IConfig)

      state.modals = modalsFor(bootConfig as IConfig)
      state.customerConfigSessionId = undefined
    },
    setStoreName: (state, action: PayloadAction<string>) => {
      state.storeName = action.payload
    },
    setValidators: (state, action: PayloadAction<string[]>) => {
      state.validators = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.secondaryLoader = action.payload
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    setCartAlerts: (state, action) => {
      state.cartAlerts = action.payload
    },
    addAlert: (state, { payload }: PayloadAction<IAlert>) => {
      const key = new Date().getTime().toString()
      state.alerts = [...state.alerts, { ...payload, key }] as Draft<IAlert>[]
    },
    removeAlert: (state, { payload }: PayloadAction<void>) => {
      state.alerts.shift()
    },
    setLoginSubmitting: (state) => {
      state.loginStatus = 'submitting'
      state.loginError = undefined
      state.loginRateLimitRetryAfterSeconds = undefined
    },
    setLoginError: (state, { payload }: PayloadAction<{ message: string }>) => {
      state.loginStatus = 'error'
      state.loginError = payload.message
      state.loginRateLimitRetryAfterSeconds = undefined
    },
    setLoginRateLimited: (
      state,
      { payload }: PayloadAction<{ retryAfterSeconds: number }>,
    ) => {
      state.loginStatus = 'rateLimited'
      state.loginRateLimitRetryAfterSeconds = payload.retryAfterSeconds
      state.loginError = undefined
    },
    clearLoginState: (state) => {
      state.loginStatus = 'idle'
      state.loginError = undefined
      state.loginRateLimitRetryAfterSeconds = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConfig.pending, (state, action) => {
      state.configRequestId = action.meta.requestId
    })
    builder.addCase(fetchConfig.fulfilled, (state, action) => {
      // A slower load for a storefront the shopper has since left must not
      // render its config under the newer URL. `meta` is optional-chained
      // because a hand-built action (in tests) may carry none.
      if (isSupersededConfigLoad(state, action.meta?.requestId)) {
        return
      }

      applyConfig(state, action.payload)
      // The slug the stored config belongs to (COM-1355). Written here so
      // the session effect's prefetch can tell a config-for-the-URL-slug
      // from the previous storefront's config lingering during an a → b
      // cross-navigation. `action.meta.arg` is the original thunk arg;
      // `arg?.slug` is defensive against a hand-built fulfilled with no
      // meta, matching the `?.` on `requestId` above.
      state.configSlug = action.meta?.arg?.slug
      state.bootConfig = action.payload as Draft<IConfig>
      state.customerConfigSessionId = undefined
      // Clear a boot error that a now-superseded earlier load raised. The
      // raising load's thunk invocation rejects, so its own requestId cannot
      // reach `.fulfilled` — the `!==` check is defensive and matches "only
      // the newest successful fetchConfig clears it". An unrelated
      // `globalError` raised with a different translation left no id behind
      // and so is not touched here.
      if (
        state.bootErrorRequestId &&
        state.bootErrorRequestId !== action.meta?.requestId
      ) {
        state.globalError = false
        state.errorMessage = null
        state.showErrorTitle = false
        state.onErrorCallback = undefined
        state.bootErrorRequestId = undefined
      }
    })
    builder.addCase(
      refetchConfigForCustomer.fulfilled,
      (state, { payload }) => {
        if (isStaleCustomerConfigLoad(state, payload)) {
          return
        }

        applyConfig(state, payload.config)
        state.modals = modalsFor(payload.config)
        state.customerConfigSessionId = payload.baseSessionId
      },
    )
  },
})

export const {
  setSessionId,
  updateParams,
  openModal,
  closeModal,
  openDialog,
  closeDialog,
  setModals,
  restoreBootConfig,
  setStoreName,
  setSessionData,
  setValidators,
  setLoading,
  setErrorMessage,
  setCartAlerts,
  addLoader,
  removeLoader,
  setLoadingDelay,
  manageErrors,
  addAlert,
  removeAlert,
  setCheckoutRules,
  setSessionRulePick,
  dropSessionRulePick,
  setLoyaltyPointsToEarn,
  setLoginSubmitting,
  setLoginError,
  setLoginRateLimited,
  clearLoginState,
} = defaultSlice.actions

/**
 * One entry per `PrefillCartLimitIssue`: the key at fault and how to describe
 * its authored value. A `Record` so a new issue does not compile until it has
 * a report.
 */
const PREFILL_CART_LIMIT_ISSUE_REPORTS: Record<
  PrefillCartLimitIssue,
  {
    field: 'limit' | 'sendLimit'
    describe: (authored: string | null) => string
  }
> = {
  clamped: {
    field: 'limit',
    describe: (authored) =>
      `${authored} is above ${PREFILL_CART_MAX_LIMIT}; ${PREFILL_CART_MAX_LIMIT} would be sent instead`,
  },
  rejected: {
    field: 'limit',
    describe: (authored) =>
      `${authored} is not a positive integer; no limit is sent, so the server applies its own default`,
  },
  flagNotBoolean: {
    field: 'sendLimit',
    describe: (authored) =>
      `${authored} is not a boolean; the limit is not sent`,
  },
  missingLimit: {
    field: 'limit',
    describe: () =>
      'is not authored but sendLimit is on; no limit is sent, so the server applies its own default',
  },
}

/**
 * The misconfiguration reports both config loads raise. `extraInfo` names the
 * calling thunk and, for a superseded load, its slug; each report adds its own
 * facets after it.
 */
const reportConfigMisconfigurations = ({
  data,
  config,
  extraInfo,
}: {
  // Authored, before `processConfig`; some checks must read it, not `config`.
  data: IConfig
  config: IConfig
  extraInfo: Record<string, unknown>
}) => {
  if (isHidePricesMisconfigured(config.options?.hidePrices)) {
    datadogRum.addError(
      new Error(
        'hidePrices enabled but misconfigured (missing customField/flagValue); feature inactive',
      ),
      generateDataDogContext({
        title: 'hidePrices misconfigured',
        extraInfo,
      }),
    )
  }

  // Against `data`, the authored config, not `config`. `processConfig`
  // corrects these two values, so checking its output would only ever see
  // a valid one and this report could never fire. The `hidePrices` and
  // `packages.sort` checks read fields nothing rewrites, which is why they
  // can take the processed config safely.
  if (isRecommendationLimitsMisconfigured(data?.globals?.recommendedProducts)) {
    datadogRum.addError(
      new Error(
        'globals.recommendedProducts carouselLimit/listLimit is not a positive integer; the shipped default is used instead',
      ),
      generateDataDogContext({
        title: 'recommendation limits misconfigured',
        extraInfo,
      }),
    )
  }

  // Also against `data`, so an omitted limit (not a misconfiguration)
  // stays distinguishable from a bad one even if a default is ever shipped
  // under `globals.prefillCart` again.
  const authoredPrefillCart = data?.globals?.prefillCart
  const prefillCartLimitIssue = getPrefillCartLimitIssue(authoredPrefillCart)

  if (prefillCartLimitIssue) {
    const { field, describe } =
      PREFILL_CART_LIMIT_ISSUE_REPORTS[prefillCartLimitIssue]

    // Live fallback: on `missingLimit` the key is absent and
    // `JSON.stringify(undefined)` is `undefined`, which would drop the
    // facet rather than say the value was absent.
    const authored = JSON.stringify(authoredPrefillCart?.[field]) ?? null

    datadogRum.addError(
      new Error(`globals.prefillCart.${field} ${describe(authored)}`),
      generateDataDogContext({
        title: `prefillCart.${field} ${prefillCartLimitIssue}`,
        extraInfo: {
          ...extraInfo,
          reason: prefillCartLimitIssue,
          field,
          authored,
        },
      }),
    )
  }

  if (isPackageSortMisconfigured(config.globals?.packages?.sort)) {
    datadogRum.addError(
      new Error(
        'packages.sort enabled but misconfigured (unrecognised field/direction/enabled); packages keep catalog order',
      ),
      generateDataDogContext({
        title: 'packages.sort misconfigured',
        extraInfo,
      }),
    )
  }

  // Also reads the processed config: `variant` is a literal string prop
  // the author sets and `processConfig` does not rewrite it, so the
  // processed and authored values agree. One Datadog error per misplaced
  // instance rather than one per load, because a tenant that drops two
  // summary panels into two containers has two things to fix. See
  // COM-901 and `utils/cartCheckoutSummaryPlacement.ts`.
  findMisplacedCartCheckoutSummaries(config.containers).forEach(
    ({ containerKey, section, uid }) => {
      datadogRum.addError(
        new Error(
          `CartCheckoutPanel variant='summary' at containers.${containerKey}.${section} ("${uid}") has no sibling SummaryList; the panel renders chromeless on the page background`,
        ),
        generateDataDogContext({
          title: 'CartCheckoutPanel summary variant misplaced',
          extraInfo: {
            ...extraInfo,
            containerKey,
            section,
            uid,
          },
        }),
      )
    },
  )
}

/** Every authored modal, closed and with no state. */
const modalsFor = (config: IConfig): IModals => {
  const modals: IModals = {}

  Object.keys(config.modals || {}).forEach((modalKey) => {
    modals[modalKey] = {
      show: false,
      state: {},
    }
  })

  return modals
}

export const fetchConfig = createAsyncThunk<
  IConfig,
  IGetConfigArgs,
  { state: { defaultSlice: ISliceDefault } }
>(
  'defaultSlice/fetchData',
  async (
    { slug, language },
    { rejectWithValue, dispatch, getState, requestId },
  ) => {
    /*
     * Every failed load ends on the error screen with the boot loader removed,
     * and the loader is removed at most once per load. Removing `STORE_CONFIG`
     * a second time would not be harmless: `removeLoader` does not guard a
     * missing name, and `splice(-1, 1)` drops whichever loader is last.
     *
     * Two paths could otherwise remove it twice. A request failure reaches
     * this function twice — `getConfig` calls it as its failure callback and
     * then rejects into the `catch` below — hence `bootFailureShown`. And the
     * success path's own removal can throw *after* its reducer has run: under
     * the legacy `ReactDOM.render` root a dispatch outside a React batch can
     * render synchronously from the store subscription, and a render error
     * propagates out of `dispatch` into the same `catch` — hence
     * `bootLoaderRemoved`. The error screen is still raised in that case: the
     * thunk rejects, so the config never reaches the store, and the reload
     * callback is the only way forward.
     *
     * Only the newest load may end on the error screen. Config.tsx starts a
     * load per slug change without cancelling the previous one, so an older
     * load that fails after a newer one succeeded would otherwise put the
     * error screen over a working storefront. A superseded load still removes
     * the loader it added, under the same once-only flags.
     */
    const isSuperseded = () =>
      isSupersededConfigLoad(getState().defaultSlice, requestId)
    // Tags a superseded load's Datadog reports with its own slug, because the
    // context's storefront is read from `window.location` and names the newer
    // load by then. A load that is still the newest adds nothing, so its
    // reports are unchanged.
    const supersededInfo = () =>
      isSuperseded() ? { superseded: true, slug } : {}
    let bootFailureShown = false
    let bootLoaderRemoved = false
    const showBootFailure = () => {
      if (bootFailureShown) {
        return
      }
      bootFailureShown = true
      if (!isSuperseded()) {
        dispatch(
          manageErrors({
            errorMessageTranslation: ErrorMessageTranslation.ErrorOnInitialLoad,
            globalError: true,
            showErrorTitle: true,
            onErrorCallback: () => window.location.reload(),
            requestId,
          }),
        )
      }
      if (!bootLoaderRemoved) {
        dispatch(removeLoader(LoadersType.STORE_CONFIG))
      }
    }
    try {
      dispatch(addLoader(LoadersType.STORE_CONFIG))
      dispatch(setStoreName(slug))
      const { data } = await getConfig(slug, showBootFailure)
      const config = processConfig(data, language, supersededInfo())
      reportConfigMisconfigurations({
        data,
        config,
        extraInfo: { function: 'fetchConfig', ...supersededInfo() },
      })

      if (isSuperseded()) {
        /*
         * A newer load owns the store: its `setModals` must stand, and the
         * reducer drops this load's `fulfilled`. The misconfiguration reports
         * above still went out, tagged with this load's slug, since the
         * config is this storefront's whether or not the shopper stayed. The
         * theme cache is keyed by this load's own slug, so writing it is
         * still correct.
         */
        writeCachedTheme(slug, config.theme)
        bootLoaderRemoved = true
        dispatch(removeLoader(LoadersType.STORE_CONFIG))
        return config
      }
      dispatch(setModals(modalsFor(config)))
      /*
       * Remember the palette so the next cold open on this storefront paints
       * branded on its first frame rather than neutral (COM-1110).
       * `refetchConfigForCustomer` writes it too, with the customer config
       * (COM-1709). This is where the resolved boot theme exists beside the
       * slug it belongs to, and `extraReducers` cannot do it because a reducer
       * has to stay pure.
       * `writeCachedTheme` swallows its own storage errors, so nothing here
       * depends on it succeeding.
       */
      writeCachedTheme(slug, config.theme)
      // Set before the dispatch, not after it: a throw out of `dispatch` comes
      // from a subscriber, after the reducer has already removed the loader,
      // so a flag set on the next line would never be set when it matters.
      bootLoaderRemoved = true
      dispatch(removeLoader(LoadersType.STORE_CONFIG))
      return config
    } catch (error) {
      /*
       * A superseded failure is still reported, since it is a real failure
       * of this storefront's config, but tagged with its own slug because the
       * context's storefront is the newer one's. It does not `startView`:
       * that would rename the RUM view the newer load is running in.
       */
      const superseded = isSuperseded()
      const context = generateDataDogContext({
        title: 'could not load config',
        extraInfo: { function: 'fetchConfig', ...supersededInfo() },
      })
      if (!superseded) {
        datadogRum.startView(context.viewName)
      }
      datadogRum.addError(error, context)
      // Before this, a throw after the request succeeded (in `processConfig`,
      // say) rejected with the loader still up and no error set: a splash that
      // never cleared and nothing to retry from.
      showBootFailure()
      return rejectWithValue(error)
    }
  },
)

/**
 * How long the boot bake waits for the customer config before keeping the
 * boot one. The same budget as `PENDING_CART_WRITES_WAIT_MS`.
 */
export const CUSTOMER_CONFIG_WAIT_MS = 3000

/**
 * Reloads the config with the session's customer code so SCS serves that
 * customer's bundle (COM-1709); dispatched through `applyConfigForSession`.
 *
 * - No loader: `STORE_CONFIG` would de-register the route table (COM-858);
 *   the prefetch's `INITIAL_DATA` already covers the wait.
 * - Never writes `configRequestId` or `storeName`, so it never supersedes a
 *   `fetchConfig`.
 * - Not started (`condition`) when the slug or the session moved since the
 *   attempt began, or a `fetchConfig` is still pending: on a slug change one
 *   can be, and its later `fulfilled` would overwrite this result. Skipping
 *   serves newest-published, as today.
 * - Dropped when stale: a `fetchConfig` started during the flight
 *   (`baseRequestId`) or the session moved (`baseSessionId`, the attempt's).
 *   The theme cache is not written either.
 * - Failure, or no answer within `CUSTOMER_CONFIG_WAIT_MS`, restores the boot
 *   config (`restoreBootConfig`) and only reports to Datadog, never with the
 *   customer code. So does a customer config that changes
 *   `options.usePaginatedCategories`, which picked the prefetch hook.
 */
export const refetchConfigForCustomer = createAsyncThunk<
  { config: IConfig; baseRequestId?: string; baseSessionId?: string },
  IRefetchConfigArgs,
  { state: { defaultSlice: ISliceDefault } }
>(
  'defaultSlice/refetchForCustomer',
  async (
    { slug, language, customerCode, sessionId: baseSessionId },
    { dispatch, getState, rejectWithValue },
  ) => {
    const { configRequestId: baseRequestId } = getState().defaultSlice

    const isStale = () =>
      isStaleCustomerConfigLoad(getState().defaultSlice, {
        baseRequestId,
        baseSessionId,
      })

    const staleInfo = () => (isStale() ? { superseded: true, slug } : {})

    try {
      // Bounded: the bake waits on this, and axios has no timeout of its own.
      // No retries, so a failure reports its own cause instead of running out
      // the clock, and the deadline cancels the request rather than orphan it.
      const controller = new AbortController()

      let timer: ReturnType<typeof setTimeout> | undefined

      const timedOut = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort()
          reject(new Error('customer config request timed out'))
        }, CUSTOMER_CONFIG_WAIT_MS)
      })

      const { data } = await Promise.race([
        getConfig(slug, undefined, {
          customerCode,
          retries: 0,
          signal: controller.signal,
        }),
        timedOut,
      ]).finally(() => clearTimeout(timer))

      const config = processConfig(data, language, {
        configSource: 'customerBundle',
        ...staleInfo(),
      })

      reportConfigMisconfigurations({
        data,
        config,
        extraInfo: { function: 'refetchConfigForCustomer', ...staleInfo() },
      })

      // `Config` chose the prefetch hook from the boot config and its held
      // key stops a re-run, so a customer config that flips it would bake the
      // store in the wrong shape: keep the boot config. The page size needs
      // no guard, since the catalogue is requested after this applies. A
      // stale result is dropped by the reducer anyway.
      const { bootConfig, config: currentConfig } = getState().defaultSlice

      const shape = {
        bootPaginated: !!(bootConfig ?? currentConfig)?.options
          ?.usePaginatedCategories,
        customerPaginated: !!config.options?.usePaginatedCategories,
      }

      if (shape.bootPaginated !== shape.customerPaginated && !isStale()) {
        const title =
          'customer config changes the boot prefetch shape; kept the boot config'

        const context = generateDataDogContext({
          title,
          extraInfo: { function: 'refetchConfigForCustomer', slug, ...shape },
        })

        // Manual RUM views drop an error raised while no view is active.
        datadogRum.startView(context.viewName)
        datadogRum.addError(new Error(title), context)
        dispatch(restoreBootConfig({ sessionId: baseSessionId }))

        return rejectWithValue(title)
      }

      // The reducer drops a stale result, but the theme cache is keyed by
      // slug alone, so a stale write would leave a palette nobody applied
      // for the next cold open.
      if (!isStale()) {
        writeCachedTheme(slug, config.theme)
      }

      return { config, baseRequestId, baseSessionId }
    } catch (error) {
      // As in `fetchConfig`: a stale failure does not `startView`, which
      // would rename the view the newer load is running in.
      const stale = isStale()

      const context = generateDataDogContext({
        title: 'could not refetch config for customer',
        extraInfo: {
          function: 'refetchConfigForCustomer',
          slug,
          ...staleInfo(),
        },
      })

      if (!stale) {
        datadogRum.startView(context.viewName)
        dispatch(restoreBootConfig({ sessionId: baseSessionId }))
      }

      datadogRum.addError(error, context)
      return rejectWithValue(error)
    }
  },
  {
    condition: ({ slug, sessionId }, { getState }) => {
      const {
        storeName,
        sessionId: currentSessionId,
        loaders,
      } = getState().defaultSlice

      return (
        storeName === slug &&
        currentSessionId === sessionId &&
        !loaders.includes(LoadersType.STORE_CONFIG)
      )
    },
  },
)

/**
 * Puts the config a session should boot under in place before the prefetch
 * requests the catalogue and bakes (COM-1709): the customer's bundle when the
 * session carries one, the boot config otherwise. Reads that do not depend on
 * the config are already in flight. Never rejects.
 *
 * The identity change re-runs `Config`'s effect while the attempt still holds
 * its key, so it causes no retry. A later re-run after a failed attempt (a
 * navigation, `setSessionData`) does retry; skipping the refetch when this
 * session's customer config is already applied keeps that retry from moving
 * the config again.
 */
export const applyConfigForSession = createAsyncThunk<
  void,
  IApplyConfigForSessionArgs,
  { state: { defaultSlice: ISliceDefault } }
>(
  'defaultSlice/applyConfigForSession',
  async ({ session, slug, language, sessionId }, { dispatch, getState }) => {
    const customerCode = customerCodeForConfig(session)

    if (!customerCode) {
      dispatch(restoreBootConfig({ sessionId }))

      return
    }

    if (getState().defaultSlice.customerConfigSessionId === sessionId) {
      return
    }

    await dispatch(
      refetchConfigForCustomer({ slug, language, customerCode, sessionId }),
    )
  },
)

export default defaultSlice.reducer
